import { Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { filter, map, shareReplay, switchMap, throttleTime } from "rxjs/operators";
import { TOKEN_BEST_ID } from "@helpers/constants";
import { convertTradableToFiat } from "@helpers/tradable-convert";
import { CryptocoinService } from "@main-services/currency";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { Cryptocoin, Fiat } from "@models";
import { AccountService } from "@modules/account/services/account.service";
import { BestRemote } from "../infrastructure/best-remote.service";
import { BestStateService } from "./best.state.service";
import { BestModel } from "../entities/best.model";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";

export interface BestPageViewModel extends BestModel {
	defaultFiat: Fiat;
	bestAsset: Cryptocoin;
	hasEnoughFundsToSwap: boolean;
	hasFiatFunds: boolean;
}

@UntilDestroy()
@Injectable({ providedIn: "root" })
export class BestFacade {
	constructor(
		private readonly bestStateService: BestStateService,
		private readonly bestRemote: BestRemote,
		private readonly accountService: AccountService,
		private readonly cryptocoinService: CryptocoinService,
		private readonly tradableWalletListService: TradableWalletListService,
		private readonly tradableListService: TradableListService,
		private readonly hasFiatFundsService: UserFiatFundsService
	) {}

	public getBestState$(): Observable<BestModel> {
		return this.bestStateService.getState$();
	}

	public getBestPageViewModel$(): Observable<BestPageViewModel> {
		return combineLatest([this.getBestState$()]).pipe(
			filter(([bestModel]) => !!bestModel), // don't build a view model on an empty state
			map(([bestModel]) => {
				const defaultFiat = this.accountService.getDefaultFiat();

				return {
					...bestModel,
					defaultFiat,
					hasFiatFunds: this.hasFiatFundsService.hasFiatWalletsWithFunds(),
					bestAsset: this.cryptocoinService.getItemById(TOKEN_BEST_ID),
					hasEnoughFundsToSwap: this.getHasEnoughFoundsToSwap(defaultFiat)
				};
			}),
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}

	public initAutoUpdates(): void {
		this.tradableWalletListService
			.getList()
			.pipe(
				// wallets emit 10+ times on initial load and this is the only way to prevent 10+ calls to the API
				throttleTime(10000),
				switchMap(() => this.getBestRemote$()),
				untilDestroyed(this) // this is a component instance service, thus will be destroyed when component is destroyed
			)
			.subscribe((bestModel) => {
				this.bestStateService.updateState(bestModel);
			});
	}

	public fetchBest(): void {
		this.getBestRemote$().subscribe((bestModel) => {
			this.bestStateService.updateState(bestModel);
		});
	}

	public getBestRemote$(): Observable<BestModel> {
		return this.bestRemote.getBest$();
	}

	public getHasEnoughFoundsToSwap(defaultFiat: Fiat): boolean {
		// only search in non-dummy wallets and wallets that have funds
		const wallets = this.tradableWalletListService.list.filter((w) => !w.isNew() && w.getBalance() > 0);

		return wallets.some((wallet) => {
			const asset = this.tradableListService.getItemById(wallet.getCurrencyId());
			if (!asset || asset.getId() === TOKEN_BEST_ID) {
				return false;
			}
			return (
				convertTradableToFiat(asset, wallet.getBalance(), defaultFiat) >= asset.getMinSell() &&
				asset.isSellActive()
			);
		});
	}
}
